import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Play, Download, Loader2, Check, Calendar, FileText } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import InputMask from 'react-input-mask';
import Sidebar from '@/components/Sidebar';

// Check if field is a system field and get its current value using user's timezone
const getSystemFieldValue = (fieldName, userTimezone = 'UTC') => {
  try {
    const now = new Date();
    const options = { timeZone: userTimezone };
    
    // Get date parts in user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      ...options,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const day = parts.find(p => p.type === 'day')?.value || '01';
    const month = parts.find(p => p.type === 'month')?.value || '01';
    const year = parts.find(p => p.type === 'year')?.value || '2026';
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[parseInt(month, 10) - 1];
    
    switch (fieldName) {
      case 'system_date_dd_mm_yyyy':
        return `${day}/${month}/${year}`;
      case 'system_date_mm_dd_yyyy':
        return `${month}/${day}/${year}`;
      case 'system_date_dd_mmm_yyyy':
        return `${day} ${monthShort}, ${year}`;
      default:
        return null;
    }
  } catch (error) {
    // Fallback to simple formatting
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    switch (fieldName) {
      case 'system_date_dd_mm_yyyy':
        return `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
      case 'system_date_mm_dd_yyyy':
        return `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()}`;
      case 'system_date_dd_mmm_yyyy':
        return `${pad(now.getDate())} ${months[now.getMonth()]}, ${now.getFullYear()}`;
      default:
        return null;
    }
  }
};

const isSystemField = (fieldName) => fieldName?.startsWith('system_');

// Evaluate conditional logic for a field
const evaluateCondition = (condition, formData, allFields) => {
  const sourceField = allFields.find(f => f.field_id === condition.field_id);
  if (!sourceField) return true; // If source field not found, show the field
  
  const sourceValue = formData[sourceField.field_name] || '';
  const conditionValue = condition.value || '';
  
  switch (condition.operator) {
    case 'equals':
      return sourceValue === conditionValue;
    case 'not_equals':
      return sourceValue !== conditionValue;
    case 'contains':
      return sourceValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'not_contains':
      return !sourceValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'is_empty':
      return !sourceValue || sourceValue.trim() === '';
    case 'is_not_empty':
      return sourceValue && sourceValue.trim() !== '';
    case 'greater_than':
      return parseFloat(sourceValue) > parseFloat(conditionValue);
    case 'less_than':
      return parseFloat(sourceValue) < parseFloat(conditionValue);
    default:
      return true;
  }
};

const shouldShowField = (field, formData, allFields) => {
  const logic = field.conditional_logic;
  
  // If no conditional logic or not enabled, show the field
  if (!logic || !logic.enabled || !logic.conditions || logic.conditions.length === 0) {
    return true;
  }
  
  // Evaluate all conditions
  const results = logic.conditions.map(condition => evaluateCondition(condition, formData, allFields));
  
  // Combine results based on logic type (and/or)
  let conditionsMet;
  if (logic.logic === 'or') {
    conditionsMet = results.some(r => r);
  } else {
    conditionsMet = results.every(r => r);
  }
  
  // Apply action (show or hide)
  if (logic.action === 'hide') {
    return !conditionsMet; // Hide when conditions are met
  } else {
    return conditionsMet; // Show when conditions are met
  }
};

const TemplateRun = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const caseId = searchParams.get('caseId');
  
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [fieldSources, setFieldSources] = useState({});  // Track which fields came from case data
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [runId, setRunId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [userDateFormat, setUserDateFormat] = useState('auto');
  const [caseInfo, setCaseInfo] = useState(null);

  // Format date for display based on user preferences
  const formatDateForDisplay = useCallback((dateValue) => {
    if (!dateValue) return dateValue;
    
    try {
      // Parse date (HTML date inputs use YYYY-MM-DD)
      let year, month, day;
      if (dateValue.includes('-') && dateValue.length === 10) {
        [year, month, day] = dateValue.split('-');
      } else {
        return dateValue;
      }
      
      // Determine actual format
      let format = userDateFormat;
      if (format === 'auto') {
        // Simple region-based detection
        if (userTimezone.startsWith('America/') && 
            ['New_York', 'Chicago', 'Denver', 'Los_Angeles', 'Phoenix'].some(city => userTimezone.includes(city))) {
          format = 'MM/DD/YYYY';
        } else if (['Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai'].some(tz => userTimezone.startsWith(tz))) {
          format = 'YYYY-MM-DD';
        } else {
          format = 'DD/MM/YYYY';
        }
      }
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthShort = months[parseInt(month, 10) - 1];
      
      switch (format) {
        case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
        case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
        case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
        case 'DD MMM YYYY': return `${day} ${monthShort} ${year}`;
        case 'MMM DD, YYYY': return `${monthShort} ${day}, ${year}`;
        default: return dateValue;
      }
    } catch (e) {
      return dateValue;
    }
  }, [userTimezone, userDateFormat]);

  const fetchTemplate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch user to get timezone and date format
      const userRes = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserTimezone(userData.timezone || 'UTC');
        setUserDateFormat(userData.date_format || 'auto');
      }
      
      // If we have a caseId, fetch pre-populated data from case context
      if (caseId) {
        const caseRunRes = await fetch(`${API}/cases/${caseId}/run-template/${templateId}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (caseRunRes.ok) {
          const caseRunData = await caseRunRes.json();
          const data = caseRunData.template;
          
          // Ensure sections exist
          if (!data.sections || data.sections.length === 0) {
            const defaultSectionId = `sec_default`;
            data.sections = [{ section_id: defaultSectionId, title: 'Form', description: null, order: 0 }];
            data.fields = data.fields.map(f => ({ ...f, section_id: f.section_id || defaultSectionId }));
          }
          
          setTemplate(data);
          setFormData(caseRunData.pre_filled_data || {});
          setFieldSources(caseRunData.field_sources || {});
          setCaseInfo({ case_id: caseId });
          return;
        }
      }
      
      // Standard template fetch (no case context)
      const response = await fetch(`${API}/templates/${templateId}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load template');
      const data = await response.json();
      
      // Ensure sections exist
      if (!data.sections || data.sections.length === 0) {
        const defaultSectionId = `sec_default`;
        data.sections = [{ section_id: defaultSectionId, title: 'Form', description: null, order: 0 }];
        data.fields = data.fields.map(f => ({ ...f, section_id: f.section_id || defaultSectionId }));
      }
      
      setTemplate(data);
      
      const initialData = {};
      data.fields.forEach(field => {
        initialData[field.field_name] = field.default_value || '';
      });
      setFormData(initialData);
    } catch (error) {
      toast.error(error.message);
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  }, [templateId, navigate, caseId]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Apply trimming based on field settings
      const processedFormData = { ...formData };
      template.fields.forEach(field => {
        const value = processedFormData[field.field_name];
        if (value && typeof value === 'string') {
          // Trim if trim_value is true or undefined (default is true)
          if (field.trim_value !== false && (field.field_type === 'text' || field.field_type === 'textarea')) {
            processedFormData[field.field_name] = value.trim();
          }
        }
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${templateId}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: templateId,
          field_data: processedFormData,
          case_id: caseId || null
        })
      });

      if (!response.ok) throw new Error('Failed to run template');
      const run = await response.json();
      setRunId(run.run_id);
      
      // If running from a case, auto-save the document to the case
      if (caseId) {
        const documentName = `${template.name}_${new Date().toISOString().split('T')[0]}`;
        await fetch(`${API}/cases/${caseId}/documents`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            document_name: documentName,
            template_id: templateId,
            run_id: run.run_id,
            file_format: 'docx'
          })
        });
        toast.success('Document generated and saved to case!');
      } else {
        toast.success('Template executed successfully!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async (format = 'docx') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${templateId}/export/${runId}?format=${format}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download document');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? `${template.name}_${runId}.pdf` : `${template.name}_${runId}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Document downloaded as ${format.toUpperCase()}!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.field_id,
      required: field.required,
      disabled: field.read_only,
      'data-testid': `field-input-${field.field_name}`
    };

    switch (field.field_type) {
      case 'textarea':
        return (
          <Textarea
            {...commonProps}
            value={formData[field.field_name] || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
            placeholder={field.placeholder}
            rows={4}
            minLength={field.min_length || undefined}
            maxLength={field.max_length || undefined}
            className={`rounded-xl border-2 border-slate-200 focus:border-violet-500 ${field.read_only ? 'bg-slate-50 cursor-not-allowed' : ''}`}
          />
        );
      
      case 'date':
        const dateValue = formData[field.field_name] || '';
        const formattedDate = dateValue ? formatDateForDisplay(dateValue) : '';
        return (
          <div>
            <Input
              {...commonProps}
              type="date"
              value={dateValue}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
              className={`h-11 rounded-xl border-2 border-slate-200 focus:border-violet-500 ${field.read_only ? 'bg-slate-50 cursor-not-allowed' : ''}`}
            />
            {dateValue && (
              <p className="text-xs text-slate-500 mt-1">
                Will appear in document as: <strong className="text-slate-700">{formattedDate}</strong>
              </p>
            )}
          </div>
        );
      
      case 'select':
      case 'dropdown':
        return (
          <select
            {...commonProps}
            value={formData[field.field_name] || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
            className={`w-full h-11 rounded-xl border-2 border-slate-200 focus:border-violet-500 px-4 ${field.read_only ? 'bg-slate-50 cursor-not-allowed' : 'bg-white'}`}
          >
            <option value="">Select an option</option>
            {(field.options || []).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => (
              <label key={idx} className={`flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors ${field.read_only ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={formData[field.field_name] === option}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                  className="w-4 h-4 text-violet-600 focus:ring-violet-500"
                  required={field.required}
                  disabled={field.read_only}
                />
                <span className="text-sm text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => (
              <label key={idx} className={`flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors ${field.read_only ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  value={option}
                  checked={(formData[field.field_name] || '').includes(option)}
                  onChange={(e) => {
                    const current = formData[field.field_name] || '';
                    const values = current ? current.split(', ') : [];
                    if (e.target.checked) {
                      values.push(option);
                    } else {
                      const index = values.indexOf(option);
                      if (index > -1) values.splice(index, 1);
                    }
                    setFormData(prev => ({ ...prev, [field.field_name]: values.join(', ') }));
                  }}
                  className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                  disabled={field.read_only}
                />
                <span className="text-sm text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        );
      
      default:
        if (field.input_mask) {
          const formatChars = {
            '#': '[0-9]',
            '@': '[A-Za-z]',
            '*': '[A-Za-z0-9]'
          };
          
          return (
            <InputMask
              mask={field.input_mask}
              formatChars={formatChars}
              value={formData[field.field_name] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
              disabled={field.read_only}
            >
              {(inputProps) => (
                <Input
                  {...inputProps}
                  {...commonProps}
                  type={field.field_type}
                  placeholder={field.placeholder}
                  minLength={field.min_length || undefined}
                  maxLength={field.max_length || undefined}
                  className={`h-11 rounded-xl border-2 border-slate-200 focus:border-violet-500 ${field.read_only ? 'bg-slate-50 cursor-not-allowed' : ''}`}
                />
              )}
            </InputMask>
          );
        }
        
        return (
          <Input
            {...commonProps}
            type={field.field_type}
            value={formData[field.field_name] || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
            placeholder={field.placeholder}
            minLength={field.min_length || undefined}
            maxLength={field.max_length || undefined}
            className={`h-11 rounded-xl border-2 border-slate-200 focus:border-violet-500 ${field.read_only ? 'bg-slate-50 cursor-not-allowed' : ''}`}
          />
        );
    }
  };

  const renderFieldWithLabel = (field) => {
    // Skip hidden fields
    if (field.hidden) {
      return null;
    }
    
    // Skip fields that don't meet conditional logic
    if (!shouldShowField(field, formData, template?.fields || [])) {
      return null;
    }
    
    const systemValue = getSystemFieldValue(field.field_name, userTimezone);
    const isSystem = isSystemField(field.field_name);
    
    // System fields - show as read-only with auto-populated value
    if (isSystem && systemValue) {
      return (
        <div key={field.field_id} data-testid={`field-wrapper-${field.field_name}`} className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Auto
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-amber-200 text-slate-700 font-medium">
              {systemValue}
            </div>
          </div>
          <p className="text-xs text-amber-600 mt-2">
            This field will be automatically filled with the current date when you export the document.
          </p>
        </div>
      );
    }
    
    // Regular fields
    const isCaseFilled = fieldSources[field.field_name];
    
    return (
      <div key={field.field_id} data-testid={`field-wrapper-${field.field_name}`} className={isCaseFilled ? 'p-3 rounded-xl bg-indigo-50/50 border border-indigo-100' : ''}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Label htmlFor={field.field_id} className="text-sm font-medium text-slate-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {isCaseFilled && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                  From Case
                </span>
              )}
              {field.input_mask && (
                <span className="text-xs text-violet-600 font-normal">(Format: {field.input_mask})</span>
              )}
            </div>
            {field.description && (
              <p className="text-xs text-slate-500 mt-0.5">{field.description}</p>
            )}
            {isCaseFilled && (
              <p className="text-xs text-indigo-600 mt-0.5">Auto-filled from: {fieldSources[field.field_name]}</p>
            )}
          </div>
          {field.read_only && (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium ml-2">Read-only</span>
          )}
        </div>
        {renderField(field)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const sortedSections = [...(template.sections || [])].sort((a, b) => a.order - b.order);
  const isWizardMode = template.display_mode === 'wizard' && sortedSections.length > 1;
  const currentSection = isWizardMode ? sortedSections[currentStep] : null;
  const totalSteps = sortedSections.length;

  const getFieldsForSection = (sectionId) => {
    return template.fields
      .filter(f => f.section_id === sectionId)
      .sort((a, b) => a.order - b.order);
  };

  const validateCurrentStep = () => {
    if (!currentSection) return true;
    const sectionFields = getFieldsForSection(currentSection.section_id);
    for (const field of sectionFields) {
      // Skip validation for system fields (auto-populated)
      if (isSystemField(field.field_name)) continue;
      
      if (field.required && !formData[field.field_name]) {
        toast.error(`Please fill in "${field.label}"`);
        return false;
      }
    }
    return true;
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (validateCurrentStep() && currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = (e) => {
    if (e) e.preventDefault();
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (e, stepIndex) => {
    if (e) e.preventDefault();
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (stepIndex === currentStep + 1 && validateCurrentStep()) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-14">
            <Button 
              variant="ghost" 
              onClick={() => caseId ? navigate(`/cases/${caseId}`) : navigate('/templates')} 
              className="rounded-lg" 
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {caseId ? 'Back to Case' : 'Back to Templates'}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Fill Template</h1>
          <p className="text-slate-500 mt-1">{template.name}</p>
          {caseId && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-100 text-indigo-700 text-sm font-medium">
              <FileText className="w-4 h-4" />
              Generating for Case - Fields auto-filled from case data
            </div>
          )}
        </div>

        {/* Wizard Steps Indicator */}
        {isWizardMode && !runId && (
          <div className="mb-8" data-testid="wizard-steps">
            <div className="flex items-center justify-between">
              {sortedSections.map((section, idx) => (
                <div key={section.section_id} className="flex items-center flex-1">
                  <button
                    type="button"
                    onClick={(e) => handleStepClick(e, idx)}
                    disabled={idx > currentStep + 1}
                    className={`flex flex-col items-center ${idx <= currentStep + 1 ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    data-testid={`step-indicator-${idx}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                        idx < currentStep
                          ? 'bg-green-500 text-white'
                          : idx === currentStep
                          ? 'bg-violet-600 text-white ring-4 ring-violet-100'
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {idx < currentStep ? <Check className="w-5 h-5" /> : idx + 1}
                    </div>
                    <span className={`mt-2 text-xs font-medium max-w-[80px] text-center truncate ${
                      idx === currentStep ? 'text-violet-700' : 'text-slate-500'
                    }`}>
                      {section.title}
                    </span>
                  </button>
                  {idx < sortedSections.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${idx < currentStep ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Form Content */}
          {isWizardMode && !runId ? (
            /* Wizard Mode - Show Current Section */
            <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="wizard-form">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">{currentSection.title}</h2>
                {currentSection.description && (
                  <p className="text-sm text-slate-500 mt-1">{currentSection.description}</p>
                )}
              </div>
              <div className="space-y-5">
                {getFieldsForSection(currentSection.section_id).map(renderFieldWithLabel)}
              </div>
              
              {/* Wizard Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="h-11 px-6 rounded-xl"
                  data-testid="wizard-prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                {currentStep < totalSteps - 1 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="wizard-next-btn"
                  >
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="h-11 px-6 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-medium"
                    data-testid="generate-doc-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Generate Document
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          ) : !runId ? (
            /* Long Form Mode - Show All Sections */
            <div className="space-y-6" data-testid="long-form">
              {sortedSections.map((section) => {
                const sectionFields = getFieldsForSection(section.section_id);
                if (sectionFields.length === 0) return null;
                
                return (
                  <div key={section.section_id} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid={`section-${section.section_id}`}>
                    <div className="mb-5">
                      <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
                      {section.description && (
                        <p className="text-sm text-slate-500 mt-1">{section.description}</p>
                      )}
                    </div>
                    <div className="space-y-5">
                      {sectionFields.map(renderFieldWithLabel)}
                    </div>
                  </div>
                );
              })}
              
              {/* Submit Button for Long Form */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-medium"
                data-testid="generate-doc-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Generate Document
                  </>
                )}
              </Button>
            </div>
          ) : (
            /* Success - Download Options */
            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm text-center" data-testid="download-section">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Document Generated!</h2>
              <p className="text-slate-500 mb-6">Your document is ready. Choose a format to download.</p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {template.template_type !== 'pdf' && (
                  <Button
                    type="button"
                    onClick={() => handleExport('docx')}
                    className="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="download-docx-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download DOCX
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => handleExport('pdf')}
                  className="h-11 px-6 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => { setRunId(null); setCurrentStep(0); }}
                className="mt-6 h-10 rounded-lg"
                data-testid="run-again-btn"
              >
                Run Again
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TemplateRun;
